// ---------------------------------------------------------------------------
// LISTING EXPIRY — single source of truth for the past-target-date rule.
// ---------------------------------------------------------------------------
// A project whose target_date has passed must not sit on the public
// marketplace forever, but it must NEVER be deleted: the homeowner, an
// administrator and any contractor who already bought the lead keep full
// access under RLS.
//
// The rule is evaluated in ONE place — the mkt_listings_public view:
//
//   date_stale   = target_date < (current_date - grace_days)
//                  AND active_confirmed_at IS NULL
//   listing_open = capacity_open AND NOT (enabled AND date_stale)
//
// and browse-listings filters every LIST query on `listing_open=is.true`.
// The knobs below live in mkt_settings.key='listing_expiry', so the window is
// changed from Admin with no code deploy. This module is the ONLY place the
// shape of that row is declared — import it, never retype the keys.
// ---------------------------------------------------------------------------
import { supabase } from '@/lib/supabase';

export const EXPIRY_SETTINGS_KEY = 'listing_expiry';

export interface ExpirySettings {
  /** Master switch. false = nothing is ever hidden for being past its date. */
  enabled: boolean;
  /** Days AFTER target_date before a project drops off the public feed. */
  grace_days: number;
  /**
   * true  = an admin/homeowner confirmation (active_confirmed_at) is what
   *         brings a past-dated project back onto the marketplace.
   * false = the grace window alone decides.
   */
  require_confirmation: boolean;
}

export const DEFAULT_EXPIRY: ExpirySettings = {
  enabled: true,
  grace_days: 7,
  require_confirmation: true,
};

/** Read the live settings row. Falls back to DEFAULT_EXPIRY, never throws. */
export const loadExpirySettings = async (): Promise<ExpirySettings> => {
  const { data } = await supabase
    .from('mkt_settings')
    .select('value')
    .eq('key', EXPIRY_SETTINGS_KEY)
    .maybeSingle();
  const v = (data?.value || {}) as Partial<ExpirySettings>;
  const days = Number(v.grace_days);
  return {
    enabled: v.enabled !== false,
    grace_days: Number.isFinite(days) && days >= 0 && days <= 365 ? days : DEFAULT_EXPIRY.grace_days,
    require_confirmation: v.require_confirmation !== false,
  };
};

/** Admin-only write (enforced by RLS on mkt_settings). */
export const saveExpirySettings = async (s: ExpirySettings) => {
  const { error } = await supabase
    .from('mkt_settings')
    .upsert({ key: EXPIRY_SETTINGS_KEY, value: s, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  return error;
};

// ---------------------------------------------------------------------------
// CLASSIFICATION
// ---------------------------------------------------------------------------
// Deliberately conservative: the database records no "completed" or
// "cancelled" signal, so this NEVER invents one. Everything a human has to
// decide is surfaced as `needs_confirmation`, not silently archived.
export type ExpiryVerdict =
  | 'active_confirmed'   // a human said it is still live -> stays public
  | 'needs_confirmation' // past its date, still inside the grace window
  | 'expired_hidden'     // past date + grace elapsed -> hidden from the feed
  | 'sold_out';          // capacity reached (6 contractors) -> hidden

export interface ExpiryRow {
  id: string;
  title: string;
  city: string | null;
  state: string | null;
  trade: string;
  target_date: string | null;
  status: string;
  contractor_count: number;
  max_contractors: number;
  active_confirmed_at: string | null;
}

/** Whole days between target_date and today (negative = still in the future). */
export const daysPastTarget = (target?: string | null): number => {
  if (!target) return 0;
  const d = new Date(`${String(target).slice(0, 10)}T00:00:00Z`).getTime();
  if (!Number.isFinite(d)) return 0;
  const today = new Date();
  const utcToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.floor((utcToday - d) / 86400000);
};

export const classifyListing = (row: ExpiryRow, s: ExpirySettings): ExpiryVerdict => {
  if (row.contractor_count >= row.max_contractors) return 'sold_out';
  if (row.active_confirmed_at) return 'active_confirmed';
  if (!s.enabled) return 'needs_confirmation';
  return daysPastTarget(row.target_date) > s.grace_days ? 'expired_hidden' : 'needs_confirmation';
};

export const VERDICT_COPY: Record<ExpiryVerdict, { en: string; es: string; tone: string }> = {
  active_confirmed: { en: 'Confirmed active', es: 'Confirmado activo', tone: 'bg-emerald-50 text-emerald-700' },
  needs_confirmation: { en: 'Needs confirmation', es: 'Requiere confirmación', tone: 'bg-amber-50 text-amber-800' },
  expired_hidden: { en: 'Expired — hidden', es: 'Expirado — oculto', tone: 'bg-red-50 text-red-700' },
  sold_out: { en: 'Sold out', es: 'Cupo lleno', tone: 'bg-slate-100 text-slate-700' },
};

/**
 * The review queue: every PUBLISHED project whose target date has already
 * passed. Nothing is deleted or mutated here — this is a read.
 */
export const loadExpiryQueue = async (): Promise<ExpiryRow[]> => {
  const today = new Date().toISOString().slice(0, 10);
  const { data } = await supabase
    .from('mkt_listings')
    .select('id,title,city,state,trade,target_date,status,contractor_count,max_contractors,active_confirmed_at')
    .eq('status', 'published')
    .not('target_date', 'is', null)
    .lt('target_date', today)
    .order('target_date', { ascending: true });
  return (data || []) as ExpiryRow[];
};

/** "Still accepting contractors" — clears the stale flag, returns it to the feed. */
export const confirmStillActive = async (id: string, adminId: string) => {
  const { error } = await supabase
    .from('mkt_listings')
    .update({ active_confirmed_at: new Date().toISOString(), active_confirmed_by: adminId })
    .eq('id', id);
  return error;
};

/** Undo a confirmation (project is not actually live after all). */
export const clearConfirmation = async (id: string) => {
  const { error } = await supabase
    .from('mkt_listings')
    .update({ active_confirmed_at: null, active_confirmed_by: null })
    .eq('id', id);
  return error;
};

/**
 * Close a project for good. Status moves off 'published' so it leaves the
 * public feed permanently — the ROW IS KEPT, and the owner, admins and any
 * prior purchaser still read it under RLS.
 */
export const closeListing = async (id: string, outcome: 'completed' | 'cancelled') => {
  const { error } = await supabase.from('mkt_listings').update({ status: outcome }).eq('id', id);
  return error;
};
